import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { submitForApproval } from '@/approvals/api'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import { CellValue, cardHref } from '@/lists/cellValue'
import { postComment, searchMentionable } from '@/notifications/api'
import { AttachmentsPanel } from './AttachmentsPanel'
import { RecordPicker } from './RecordPicker'
import { overdueDays } from './CounterpartyRecordView'
import { daysUntil } from './DealRecordView'
import { HistoryPanel } from './HistoryPanel'
import {
  addInvoiceLine,
  allocatePayment,
  cancelInvoice,
  deallocate,
  fetchAdvances,
  fetchDunning,
  fetchInvoice,
  fetchSettlements,
  lineArithmetic,
  linesReconcile,
  removeInvoiceLine,
  sendInvoice,
  settledTotal,
  type DunningAction,
  type Invoice,
  type InvoiceLineInput,
  type Payment,
  type Settlement,
} from './invoiceApi'
import { AllocateForm } from './InvoiceTabs'
import {
  Composer,
  ConfirmModal,
  Feed,
  Panel,
  RecordHeader,
  RecordTabs,
  StateView,
  type ComposerMode,
  type HeaderAction,
  type MenuAction,
} from './record'
import { CopyValue, feedItemOf, formatMoment, labelOf, listPath } from './recordHelpers'
import { TimelineMore, useTimeline } from './useTimeline'
import { approvalsTab } from './TimelineTab'
import type { RecordViewProps } from './types'
import '@/styles/record.css'
import '@/styles/record-extra.css'
import { useWriteEmail } from '@/mail/WriteEmail'

/**
 * Карточка счёта на дизайн-системе (Invoice.dc.html).
 *
 * Главная строка — три суммы и шкала: сумма, оплачено, остаток. Остаток подписан словами
 * («просрочено 18 дн.» или «срок через 14 дн.»), потому что ради него счёт и открывают.
 * Все три суммы и НДС приходят с сервера: клиент их не считает, а только сверяет строки с
 * итогом и разнесения с оплаченным — и говорит вслух, когда не сходится.
 *
 * Действия по весу макета: основное — «Разнести оплату», «Отправить» — вторичное, отмена —
 * в «⋯» за чертой и через подтверждение. Каждое — только при праве и только в состоянии,
 * где домен его примет: кнопка, ведущая к гарантированному отказу, учит игнорировать отказы.
 *
 * Прежние вкладки (#72, #79) не потеряны: позиции, оплаты с разнесением и снятием,
 * напоминания, отправка и отмена, согласования, лента, поля счёта, журнал, вложения.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - «Скачать PDF», «Создать УПД», «Копировать счёт»: печатной формы счёта нет (#15 — без
 *   побайтового рендера), а выпуск УПД из счёта требует данных документа, которых карточка
 *   не собирает; копирования счёта домен не публикует.
 * - Повторная выписка — домен её не публикует (см. `InvoiceTabs`).
 * - «Пени по договору»: сервер их не считает, а своя формула стала бы вторым ответом на
 *   денежный вопрос.
 * - Подсказка сверки с банковской выпиской («п/п № 5120, назначение…»): сопоставление
 *   выписки со счётом живёт на экране сверки; здесь — авансы клиента с неразнесённым
 *   остатком в валюте счёта, то есть ровно то, что можно разнести.
 * - «Открыт клиентом», «следующее напоминание … можно отключить»: момента открытия счёта
 *   клиентом сервер не знает, а расписание будущих ступеней и их отключение API не отдаёт.
 * - Остаток платежа «→ на другой счёт (ссылка)»: разнесение отдаёт платёж и его
 *   неразнесённый остаток, но не то, на какие ещё счета он ушёл.
 * - Имя того, кто разнёс оплату: строка разнесения несёт идентификатор учётной записи без
 *   имени.
 */
export function InvoiceRecordView({ recordId, record, context, form, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const mail = useWriteEmail({ type: 'invoice', id: recordId })
  const queryClient = useQueryClient()

  const [tab, setTab] = useState('overview')
  const [allocating, setAllocating] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const mayUpdate = can(user, 'billing.invoice', 'update')
  const mayReadPayments = can(user, 'payments.payment', 'read')
  const mayAllocate = can(user, 'payments.payment', 'update')
  const maySubmitApproval = can(user, 'system.approval', 'create')
  const mayReadDocuments = can(user, 'billing.document', 'read')

  const invoiceQuery = useQuery({
    queryKey: ['invoice', recordId],
    queryFn: ({ signal }) => fetchInvoice(recordId, signal),
    retry: false,
  })
  const invoice = invoiceQuery.data
  const settlements = useQuery({
    queryKey: ['invoice-settlements', recordId],
    queryFn: ({ signal }) => fetchSettlements(recordId, signal),
    enabled: mayReadPayments,
    retry: false,
  })
  const advances = useQuery({
    queryKey: ['counterparty-advances', invoice?.counterpartyId],
    queryFn: ({ signal }) => fetchAdvances(invoice?.counterpartyId as string, signal),
    enabled: mayAllocate && Boolean(invoice?.payable && invoice.counterpartyId),
    retry: false,
  })
  const dunning = useQuery({
    queryKey: ['invoice-dunning', recordId],
    queryFn: ({ signal }) => fetchDunning(recordId, signal),
    retry: false,
  })
  const feed = useTimeline('invoice', recordId)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['invoice', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['invoice-settlements', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'billing.invoice', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'billing.invoice'] })
    void queryClient.invalidateQueries({ queryKey: ['timeline', 'invoice', recordId] })
    if (invoice) void queryClient.invalidateQueries({ queryKey: ['counterparty-advances', invoice.counterpartyId] })
  }
  const fail = (error: unknown) => {
    setNotice(null)
    setFailure(error instanceof ApiError ? error.message : t('app.error'))
  }
  const succeed = (message: string) => {
    setFailure(null)
    setNotice(message)
    refresh()
  }

  const send = useMutation({
    mutationFn: () => sendInvoice(recordId),
    onSuccess: () => succeed(t('card.inv.action.sent')),
    onError: fail,
  })
  const cancel = useMutation({
    mutationFn: () => cancelInvoice(recordId, reason.trim()),
    onSuccess: () => {
      setConfirmCancel(false)
      setCancelling(false)
      setReason('')
      succeed(t('card.inv.action.cancelled'))
    },
    onError: (error) => {
      setConfirmCancel(false)
      fail(error)
    },
  })
  // #94: the threshold is checked by the server at this moment, from the invoice's current
  // figures; below every threshold the answer is "not required", and that is said in words.
  const submitApproval = useMutation({
    mutationFn: () => submitForApproval('invoice', recordId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['approval-history', 'invoice', recordId] })
      succeed(result.required ? t('record.inv.approval.submitted') : t('record.inv.approval.notRequired'))
    },
    onError: fail,
  })
  // #94: lines of a draft are edited by operations; the answer is the recalculated invoice.
  const addLine = useMutation({
    mutationFn: (line: InvoiceLineInput) => addInvoiceLine(recordId, line),
    onSuccess: () => succeed(t('record.inv.line.added')),
    onError: fail,
  })
  const removeLine = useMutation({
    mutationFn: (lineId: string) => removeInvoiceLine(recordId, lineId),
    onSuccess: () => succeed(t('record.inv.line.removed')),
    onError: fail,
  })
  const quickAllocate = useMutation({
    mutationFn: ({ payment, amount }: { payment: Payment; amount: number }) => allocatePayment(payment.id, recordId, amount),
    onSuccess: () => succeed(t('record.inv.allocated')),
    onError: fail,
  })
  const removal = useMutation({
    mutationFn: (allocationId: string) => deallocate(allocationId),
    onSuccess: () => succeed(t('record.inv.deallocated')),
    onError: fail,
  })

  if (invoiceQuery.isLoading) {
    return (
      <div className="rec-page" aria-busy="true" aria-label={t('app.loading')}>
        <span className="skel" style={{ width: 320, height: 20 }} />
        <span className="skel" style={{ height: 92, borderRadius: 8 }} />
        <span className="skel" style={{ height: 160, borderRadius: 8 }} />
      </div>
    )
  }
  if (!invoice) {
    const forbidden = invoiceQuery.error instanceof ApiError && invoiceQuery.error.isForbidden
    return (
      <StateView
        boxed
        kind={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('record.state.forbidden.title') : t('record.state.error.title')}
        description={forbidden ? t('record.state.forbidden.text') : t('record.state.error.text')}
        actions={
          <>
            {!forbidden && (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => void invoiceQuery.refetch()}>
                <Icon name="refresh" size="sm" />
                {t('record.state.retry')}
              </button>
            )}
            <Link to={listPath('billing.invoice', '/invoices')} className="btn btn-ghost btn-sm">
              {t('record.state.toList')}
            </Link>
          </>
        }
      />
    )
  }

  const sendable = invoice.status === 'draft'
  const cancellable = invoice.status !== 'cancelled' && invoice.status !== 'paid'
  const late = overdueDays(invoice)

  // ------------------------------------------------------------------ шапка

  const counterpartyName = labelOf(record.values, 'counterparty_id')
  const counterpartyHref = cardHref(user, 'sales.counterparty', invoice.counterpartyId)
  const dealLabel = labelOf(record.values, 'deal_id')
  const dealHref = invoice.dealId ? cardHref(user, 'sales.deal', invoice.dealId) : null
  const contractLabel = labelOf(record.values, 'contract_id')
  const contractHref = invoice.contractId ? cardHref(user, 'billing.contract', invoice.contractId) : null

  const meta = [
    <CellValue
      key="status"
      values={{ status: invoice.status }}
      column="status"
      descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }}
      entity="billing.invoice"
    />,
    late > 0 ? (
      <span key="late" className="badge b-danger">
        <Icon name="clock" />
        {t('record.inv.overdueDays', { days: late })}
      </span>
    ) : null,
    <span key="client" className="row" style={{ gap: 6 }}>
      <Icon name="building" size="sm" />
      {counterpartyHref ? (
        <Link to={counterpartyHref}>{counterpartyName ?? t('record.inv.client')}</Link>
      ) : (
        (counterpartyName ?? t('record.inv.client'))
      )}
    </span>,
    invoice.dealId ? (
      <span key="deal">
        {t('record.inv.deal')}{' '}
        {dealHref ? <Link to={dealHref}>{dealLabel ?? t('record.inv.open')}</Link> : (dealLabel ?? '—')}
      </span>
    ) : null,
    invoice.contractId ? (
      <span key="contract">
        {t('record.inv.contract')}{' '}
        {contractHref ? <Link to={contractHref}>{contractLabel ?? t('record.inv.open')}</Link> : (contractLabel ?? '—')}
      </span>
    ) : null,
    <span key="dates">
      {t('record.inv.issued', { date: formatDate(invoice.issuedOn, locale) })}
      {invoice.dueOn && (
        <>
          {', '}
          {t('record.inv.payBy')}{' '}
          <span className="strong" style={{ color: 'var(--fg)' }}>
            {formatDate(invoice.dueOn, locale)}
          </span>
        </>
      )}
    </span>,
  ]

  const openAllocation = () => {
    setTab('payments')
    setAllocating(true)
  }

  // A payable invoice and the right to allocate: the action the design puts first.
  const primary: HeaderAction | null =
    mayAllocate && invoice.payable
      ? { id: 'allocate', label: t('record.inv.allocate'), onClick: openAllocation }
      : null
  const secondary: HeaderAction[] =
    mayUpdate && sendable
      ? [{ id: 'send', label: t('card.inv.action.send'), icon: 'mail', onClick: () => send.mutate(), disabled: send.isPending }]
      : []
  if (mayUpdate && maySubmitApproval && sendable) {
    secondary.push({
      id: 'approval',
      label: t('record.inv.submitApproval'),
      icon: 'check',
      onClick: () => submitApproval.mutate(),
      disabled: submitApproval.isPending,
    })
  }

  const menu: MenuAction[] = [
    {
      id: 'copy-link',
      label: t('record.copyLink'),
      icon: 'link',
      onSelect: () => {
        void navigator.clipboard?.writeText(window.location.href).catch(() => setFailure(t('record.copyFailed')))
      },
    },
  ]
  if (mayUpdate && cancellable) {
    menu.push({
      id: 'cancel',
      label: t('record.inv.cancel'),
      icon: 'close',
      danger: true,
      onSelect: () => {
        setFailure(null)
        setCancelling(true)
      },
    })
  }

  // ------------------------------------------------------------------ подсказка сверки

  // An advance in the invoice currency only: a euro payment does not close a rouble invoice
  // without a rate, and the domain would refuse it anyway.
  const candidate =
    mayAllocate && invoice.payable
      ? (advances.data ?? []).find((payment) => payment.currency === invoice.currency && payment.unallocatedAmount > 0)
      : undefined
  // A hint, not a rule: the domain refuses more than either balance, and it decides.
  const candidateAmount = candidate ? Math.min(invoice.outstanding, candidate.unallocatedAmount) : 0

  // ------------------------------------------------------------------ лента

  const mayComment = can(user, 'system.comment', 'create')
  const modes: ComposerMode[] = mayComment
    ? [
        {
          id: 'comment',
          label: t('timeline.kind.comment'),
          icon: 'comment',
          placeholder: t('record.inv.composer.comment'),
          mentions: searchMentionable,
          submit: async ({ text, mentionedUserIds }) => {
            await postComment('billing.invoice', recordId, text, { mentionedUserIds })
            void queryClient.invalidateQueries({ queryKey: ['timeline', 'invoice', recordId] })
          },
        },
      ]
    : []
  const feedItems = (feed.data?.events ?? []).map((event) => feedItemOf(event, t))

  // ------------------------------------------------------------------ вкладки

  const overview = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      <LinesPanel
        invoice={invoice}
        editable={mayUpdate && sendable}
        busy={addLine.isPending || removeLine.isPending}
        onAdd={(line) => addLine.mutate(line)}
        onRemove={(lineId) => removeLine.mutate(lineId)}
      />

      <div className="layout-2-wide">
        <div className="rec-col">
          <Panel
            title={t('record.inv.payments')}
            flush
            actions={
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab('payments')}>
                {t('record.inv.allPayments')}
              </button>
            }
          >
            <SettlementsTable invoice={invoice} query={settlements} allowed={mayReadPayments} />
          </Panel>
          {modes.length > 0 && <Composer modes={modes} />}
          {feed.isLoading ? (
            <p className="muted">{t('app.loading')}</p>
          ) : feed.isError ? (
            <StateView compact kind="error" title={t('record.inv.feed.error')} />
          ) : (
            <Feed
              items={feedItems}
              empty={<StateView compact kind="empty" title={t('record.inv.feed.empty')} />}
              footer={<TimelineMore feed={feed} />}
            />
          )}
        </div>
        <div className="rec-col">
          <Panel title={t('record.inv.dispatches')} flush>
            <Dispatches invoice={invoice} query={dunning} />
          </Panel>
          {mayReadDocuments && (
            <Panel title={t('record.tab.documents')} flush>
              <InvoiceDocuments invoiceId={recordId} />
            </Panel>
          )}
        </div>
      </div>
    </div>
  )

  const paymentsTab = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      <Panel
        title={t('record.inv.payments')}
        flush
        actions={
          mayAllocate && invoice.payable && !allocating ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAllocating(true)}>
              <Icon name="plus" size="sm" />
              {t('record.inv.allocate')}
            </button>
          ) : undefined
        }
      >
        <SettlementsTable
          invoice={invoice}
          query={settlements}
          allowed={mayReadPayments}
          onRemove={mayAllocate ? (id) => removal.mutate(id) : undefined}
          removing={removal.isPending}
        />
      </Panel>
      {mayAllocate && allocating && (
        <Panel
          title={t('card.inv.allocate.title')}
          actions={
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAllocating(false)}>
              {t('card.cancel')}
            </button>
          }
        >
          <AllocateForm
            invoice={invoice}
            advances={advances.data ?? []}
            onDone={() => {
              setAllocating(false)
              succeed(t('record.inv.allocated'))
            }}
          />
        </Panel>
      )}
      <Panel title={t('card.tab.invoiceDunning')} flush>
        <DunningTable query={dunning} />
      </Panel>
    </div>
  )

  const documentsTab = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      <Panel title={t('record.inv.details')}>{form}</Panel>
      {mayReadDocuments && (
        <Panel title={t('record.inv.primaryDocuments')} flush>
          <InvoiceDocuments invoiceId={recordId} />
        </Panel>
      )}
      <Panel title={t('card.tab.attachments')}>
        <AttachmentsPanel entityType={context.entity} entityId={recordId} resource={context.resource} />
      </Panel>
      <Panel title={t('card.tab.related')}>
        <p>{t('card.related.missing')}</p>
        <p className="muted">{t('card.related.reason')}</p>
      </Panel>
    </div>
  )

  const historyTab = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      <Panel title={t('card.tab.approvals')}>{approvalsTab('invoice').render(context)}</Panel>
      <Panel title={t('record.history.changes')}>
        <HistoryPanel entity={context.entity} recordId={recordId} />
      </Panel>
      <Panel title={t('card.inv.action.reissue')}>
        <p className="muted">{t('card.inv.action.reissueMissing')}</p>
      </Panel>
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[
          { label: t('nav.invoices'), to: listPath('billing.invoice', '/invoices') },
          { label: invoice.number, mono: true },
        ]}
        title={
          <>
            {t('record.inv.title')} <span className="mono rec-mono-title">{invoice.number}</span>
          </>
        }
        meta={[...meta, <CopyValue key="copy" value={invoice.number} label={t('record.copyNumber', { number: invoice.number })} />]}
        primary={primary}
        secondary={mail.action ? [...secondary, mail.action] : secondary}
        menu={menu}
        menuLabel={t('record.more')}
      />
      {mail.dialog}

      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      {notice && (
        <div className="alert alert-info" role="status">
          <Icon name="check" />
          <span className="fg">{notice}</span>
        </div>
      )}
      {notices}
      {invoice.status === 'cancelled' && (
        <div className="alert alert-warning" role="status">
          <Icon name="close" />
          <span>{t('record.inv.cancelledBecause', { reason: invoice.cancelReason ?? '—' })}</span>
        </div>
      )}

      {cancelling && (
        <section className="panel" role="group" aria-label={t('record.inv.cancel')}>
          <div className="panel-b stack" style={{ gap: 8 }}>
            <label className="stack" style={{ gap: 4 }}>
              <span className="t-sm">{t('card.inv.action.reason')}</span>
              <textarea className="input" rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
            </label>
            {/* The reason is required and never filled in for the user: the cancelled
                invoice stays in the register, and "why" is asked a year later. */}
            {reason.trim() === '' && <span className="t-sm muted">{t('card.inv.action.reasonRequired')}</span>}
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={reason.trim() === '' || cancel.isPending}
                onClick={() => setConfirmCancel(true)}
              >
                {t('record.inv.cancelContinue')}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setCancelling(false)
                  setReason('')
                }}
              >
                {t('card.cancel')}
              </button>
            </div>
          </div>
        </section>
      )}

      <AmountsPanel invoice={invoice} late={late} />

      {candidate && (
        <div className="alert alert-info" role="status">
          <Icon name="payments" />
          <span className="fg">
            {t('record.inv.advanceHint', {
              number: candidate.number ?? candidate.id.slice(0, 8),
              date: formatDate(candidate.paymentDate, locale),
              amount: formatMoney(candidate.unallocatedAmount, candidate.currency, locale),
            })}
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={quickAllocate.isPending}
            onClick={() => quickAllocate.mutate({ payment: candidate, amount: candidateAmount })}
          >
            {t('record.inv.allocateHere', { amount: formatMoney(candidateAmount, invoice.currency, locale) })}
          </button>
        </div>
      )}

      <RecordTabs
        tabs={[
          { id: 'overview', label: t('record.tab.overview') },
          { id: 'payments', label: t('record.inv.tab.payments'), count: settlements.data?.length ?? null },
          { id: 'documents', label: t('record.tab.documents') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.inv.sections')}
      >
        {tab === 'overview' && overview}
        {tab === 'payments' && paymentsTab}
        {tab === 'documents' && documentsTab}
        {tab === 'history' && historyTab}
      </RecordTabs>

      <ConfirmModal
        open={confirmCancel}
        icon="close"
        title={t('record.inv.cancel.title', { number: invoice.number })}
        confirmLabel={t('record.inv.cancel')}
        pending={cancel.isPending}
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => cancel.mutate()}
      >
        <p>{t('record.inv.cancel.text', { reason: reason.trim() })}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

// ------------------------------------------------------------------ суммы

/**
 * Три суммы и шкала оплаты. Доля оплаты — для ширины полосы, не денежная цифра: сумма,
 * оплачено и остаток показаны так, как их отдал сервер.
 */
function AmountsPanel({ invoice, late }: { invoice: Invoice; late: number }) {
  const { t, locale } = useApp()
  const percent = invoice.total > 0 ? Math.min(100, Math.max(0, Math.round((invoice.paidTotal / invoice.total) * 100))) : 0
  const left = invoice.dueOn ? daysUntil(invoice.dueOn) : null
  return (
    <section className="panel rec-amounts" aria-label={t('record.inv.amounts')}>
      <div className="rec-amounts-grid">
        <div className="stack" style={{ gap: 2 }}>
          <span className="t-sm muted">{t('record.inv.total')}</span>
          <span className="kpi-num">{formatMoney(invoice.total, invoice.currency, locale)}</span>
          <span className="t-sm secondary">
            {t('record.inv.vatSub', { amount: formatMoney(invoice.vatTotal, invoice.currency, locale) })}
          </span>
        </div>
        <div className="stack" style={{ gap: 2 }}>
          <span className="t-sm muted">{t('card.inv.paid')}</span>
          <span className="kpi-num">{formatMoney(invoice.paidTotal, invoice.currency, locale)}</span>
          <span className="t-sm secondary">{t('record.inv.paidPercent', { percent })}</span>
        </div>
        <div className="stack" style={{ gap: 2 }}>
          <span className="t-sm muted">{t('card.inv.outstanding')}</span>
          <span className={late > 0 ? 'kpi-num rec-danger-text' : 'kpi-num'}>
            {formatMoney(invoice.outstanding, invoice.currency, locale)}
          </span>
          {late > 0 ? (
            <span className="flag flag-danger t-sm">
              <Icon name="alert" />
              {t('record.inv.overdueBy', { days: late })}
            </span>
          ) : invoice.outstanding <= 0 ? (
            <span className="t-sm secondary">{t('record.inv.settled')}</span>
          ) : left !== null && invoice.status !== 'cancelled' ? (
            <span className="t-sm secondary">
              {left === 0 ? t('record.inv.dueToday') : t('record.inv.dueIn', { days: left })}
            </span>
          ) : null}
        </div>
      </div>
      <div
        className="meter"
        style={{ height: 8 }}
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('record.inv.paidPercent', { percent })}
      >
        <i style={{ width: `${percent}%`, background: 'var(--success)' }} />
      </div>
    </section>
  )
}

// ------------------------------------------------------------------ позиции

/**
 * Позиции с НДС построчно (ТЗ §4.3): без НДС, ставка, сумма НДС, с НДС; итоги — с сервера.
 *
 * Сумма без НДС считается по той же формуле, что на сервере (`lineArithmetic`) — у строки
 * её нет отдельным полем. Расхождение итога со строками и строки с её формулой выводится
 * предупреждением: клиент складывает строки, и молчание об этом обнаружит он.
 */
function LinesPanel({
  invoice,
  editable = false,
  busy = false,
  onAdd,
  onRemove,
}: {
  invoice: Invoice
  /** Only a draft the caller may update: a sent invoice is what the customer already holds. */
  editable?: boolean
  busy?: boolean
  onAdd?: (line: InvoiceLineInput) => void
  onRemove?: (lineId: string) => void
}) {
  const { t, locale } = useApp()
  const lines = [...(invoice.lines ?? [])].sort((a, b) => a.lineOrder - b.lineOrder)
  const drifting = lines.filter((line) => lineArithmetic(line).total.toFixed(2) !== line.lineTotal.toFixed(2))
  const reconciles = linesReconcile(invoice)
  const money = (value: number) => formatMoney(value, invoice.currency, locale)
  return (
    <Panel
      title={t('record.tab.positions')}
      count={lines.length}
      flush
      actions={<span className="t-sm muted">{t('record.inv.vatPerLine')}</span>}
    >
      {!reconciles && (
        <div className="panel-b">
          <div className="alert alert-danger" role="alert">
            <Icon name="alert" />
            <span>{t('card.inv.lines.mismatch')}</span>
          </div>
        </div>
      )}
      {drifting.length > 0 && (
        <div className="panel-b">
          <div className="alert alert-danger" role="alert">
            <Icon name="alert" />
            <span>{t('card.inv.lines.lineMismatch', { count: drifting.length })}</span>
          </div>
        </div>
      )}
      {lines.length === 0 ? (
        <div className="panel-b secondary">{t('card.inv.lines.empty')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl" aria-label={t('record.tab.positions')}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>№</th>
                <th>{t('card.inv.line.description')}</th>
                <th className="num">{t('card.inv.line.quantity')}</th>
                <th className="num">{t('card.inv.line.unitPrice')}</th>
                <th className="num">{t('card.inv.line.discount')}</th>
                <th className="num">{t('card.inv.line.net')}</th>
                <th className="num">{t('card.inv.line.vatRate')}</th>
                <th className="num">{t('card.inv.line.vatAmount')}</th>
                <th className="num">{t('card.inv.line.total')}</th>
                {editable && <th aria-label={t('record.inv.line.actions')} />}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={line.id}>
                  <td className="muted">{index + 1}</td>
                  <td className="wrap">{line.description}</td>
                  <td className="num">{formatNumber(line.quantity, locale)}</td>
                  <td className="num">{money(line.unitPrice)}</td>
                  {/* A percentage, as it is set: in roubles it would hide that VAT is charged
                      on the discounted amount. */}
                  <td className="num">{line.discountPercent > 0 ? `${line.discountPercent} %` : '—'}</td>
                  <td className="num">{money(lineArithmetic(line).net)}</td>
                  <td className="num muted">{`${line.vatRate} %`}</td>
                  <td className="num">{money(line.vatAmount)}</td>
                  <td className="num strong">{money(line.lineTotal)}</td>
                  {editable && (
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon"
                        disabled={busy}
                        aria-label={t('record.inv.line.remove', { line: line.description })}
                        onClick={() => onRemove?.(line.id)}
                      >
                        <Icon name="close" size="sm" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {/* Totals from the server, not the sum of the drawn rows: summing here would
                  paint a "right" total over a wrong one and hide the gap. */}
              <tr className="total">
                <td />
                <td>{t('card.inv.total.total')}</td>
                <td />
                <td />
                <td className="num">{invoice.discountTotal > 0 ? money(invoice.discountTotal) : ''}</td>
                <td className="num">{money(invoice.subtotal)}</td>
                <td />
                <td className="num">{money(invoice.vatTotal)}</td>
                <td className="num">{formatMoney(invoice.total, invoice.currency, locale)}</td>
                {editable && <td />}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {editable && onAdd && <AddInvoiceLine busy={busy} onAdd={onAdd} />}
    </Panel>
  )
}

/**
 * Новая позиция черновика: товар из справочника (или свободный текст), количество, цена,
 * скидка. Единица берётся у выбранного товара — строка счёта без единицы печатается с
 * пустой графой, и бухгалтер клиента её вернёт.
 */
function AddInvoiceLine({ busy, onAdd }: { busy: boolean; onAdd: (line: InvoiceLineInput) => void }) {
  const { t } = useApp()
  const [product, setProduct] = useState<{ id: string; label: string } | null>(null)
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [price, setPrice] = useState('')
  const [discount, setDiscount] = useState('0')
  const unit = useQuery({
    queryKey: ['invoice-line-product-uom', product?.id],
    enabled: product !== null,
    queryFn: ({ signal }) =>
      fetchPage(
        'sales.product',
        { columns: ['uom_id'], filters: [{ column: 'id', operator: 'eq', value: product!.id }], sort: [] },
        null,
        signal,
      ),
    retry: false,
  })
  const uomId = (unit.data?.rows[0]?.uom_id as string | undefined) ?? null
  const text = description.trim() !== '' ? description.trim() : (product?.label ?? '')
  const ready = text !== '' && Number(quantity) > 0 && price.trim() !== '' && Number(price) >= 0
  return (
    <div className="panel-b row wrap" role="group" aria-label={t('record.inv.line.add')} style={{ gap: 8, alignItems: 'flex-end' }}>
      <div className="field" style={{ minWidth: 220 }}>
        <label htmlFor="invoice-line-product">{t('card.deal.line.product')}</label>
        <RecordPicker id="invoice-line-product" entity="sales.product" searchColumn="name" extraColumn="sku"
                      value={product} onChange={setProduct} />
      </div>
      <label className="field" style={{ flex: 1, minWidth: 200 }}>
        <span>{t('card.inv.line.description')}</span>
        <input className="input" value={description} placeholder={product?.label ?? ''}
               onChange={(event) => setDescription(event.target.value)} />
      </label>
      <label className="field" style={{ width: 90 }}>
        <span>{t('card.inv.line.quantity')}</span>
        <input className="input" type="number" min="0" step="any" value={quantity}
               onChange={(event) => setQuantity(event.target.value)} />
      </label>
      <label className="field" style={{ width: 120 }}>
        <span>{t('card.inv.line.unitPrice')}</span>
        <input className="input" type="number" min="0" step="any" value={price}
               onChange={(event) => setPrice(event.target.value)} />
      </label>
      <label className="field" style={{ width: 90 }}>
        <span>{t('card.inv.line.discount')}</span>
        <input className="input" type="number" min="0" max="100" step="any" value={discount}
               onChange={(event) => setDiscount(event.target.value)} />
      </label>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={busy || !ready || (product !== null && unit.isLoading)}
        onClick={() => {
          onAdd({
            productId: product?.id ?? null,
            description: text,
            uomId,
            quantity: Number(quantity),
            unitPrice: Number(price),
            discountPercent: Number(discount) || 0,
          })
          setProduct(null)
          setDescription('')
          setQuantity('1')
          setPrice('')
          setDiscount('0')
        }}
      >
        <Icon name="plus" size="sm" />
        {t('record.inv.line.add')}
      </button>
    </div>
  )
}

// ------------------------------------------------------------------ оплаты

type QueryOf<T> = { isLoading: boolean; isError: boolean; error: unknown; data: T | undefined }

/**
 * Чем закрыт счёт: из какого платежа сколько ушло на этот счёт и сколько у платежа осталось.
 * Сумма разнесений сверяется с `paidTotal` счёта: расхождение — порча данных, и показать
 * его надо здесь, а не в квартальной сверке.
 */
function SettlementsTable({
  invoice,
  query,
  allowed,
  onRemove,
  removing,
}: {
  invoice: Invoice
  query: QueryOf<Settlement[]>
  allowed: boolean
  onRemove?: (allocationId: string) => void
  removing?: boolean
}) {
  const { t, locale } = useApp()
  if (!allowed) return <div className="panel-b muted">{t('card.inv.settlements.needsPayments')}</div>
  if (query.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (query.isError) {
    return (
      <StateView
        compact
        kind={query.error instanceof ApiError && query.error.isForbidden ? 'forbidden' : 'error'}
        title={t('record.inv.payments.error')}
      />
    )
  }
  const rows = query.data ?? []
  if (rows.length === 0) {
    return (
      <StateView
        compact
        kind="empty"
        title={t('card.inv.settlements.empty')}
        description={invoice.dueOn ? t('record.inv.payments.emptyDue', { date: formatDate(invoice.dueOn, locale) }) : undefined}
      />
    )
  }
  const settled = settledTotal(rows)
  return (
    <>
      {settled.toFixed(2) !== invoice.paidTotal.toFixed(2) && (
        <div className="panel-b">
          <div className="alert alert-danger" role="alert">
            <Icon name="alert" />
            <span>
              {t('card.inv.settlements.mismatch', { allocated: formatMoney(settled, invoice.currency, locale), paid: formatMoney(invoice.paidTotal, invoice.currency, locale) })}
            </span>
          </div>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" aria-label={t('record.inv.payments')}>
          <thead>
            <tr>
              <th>{t('card.inv.settlement.date')}</th>
              <th>{t('card.inv.settlement.payment')}</th>
              <th className="num">{t('record.inv.paymentAmount')}</th>
              <th className="num">{t('record.inv.toThisInvoice')}</th>
              <th className="num">{t('record.inv.paymentLeft')}</th>
              <th>{t('card.inv.settlement.rule')}</th>
              {onRemove && <th>{t('card.inv.settlement.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.allocation.id}>
                <td>{row.payment ? formatDate(row.payment.paymentDate, locale) : '—'}</td>
                <td>
                  {/* A broken link shows as such, not as an invented number. */}
                  {row.payment ? (
                    <span className="stack" style={{ gap: 0 }}>
                      <span className="mono">{row.payment.number ?? row.payment.id.slice(0, 8)}</span>
                      <span className="t-xs muted">{t(`payment.method.${row.payment.method}`)}</span>
                    </span>
                  ) : (
                    t('card.inv.settlement.orphan')
                  )}
                </td>
                <td className="num">{row.payment ? formatMoney(row.payment.amount, row.payment.currency, locale) : '—'}</td>
                <td className="num strong">{formatMoney(row.allocation.amount, invoice.currency, locale)}</td>
                <td className="num">
                  {row.payment ? formatMoney(row.payment.unallocatedAmount, row.payment.currency, locale) : '—'}
                </td>
                <td>{t(`allocation.rule.${row.allocation.rule}`)}</td>
                {onRemove && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={removing}
                      onClick={() => onRemove(row.allocation.id)}
                    >
                      {t('card.inv.settlement.remove')}
                    </button>
                  </td>
                )}
              </tr>
            ))}
            <tr className="total">
              <td>{t('card.deal.lines.total')}</td>
              <td />
              <td />
              <td className="num">{formatMoney(settled, invoice.currency, locale)}</td>
              <td />
              <td />
              {onRemove && <td />}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  )
}

// ------------------------------------------------------------------ отправки и напоминания

/** Отправка и ступени напоминаний — все, а не последняя: история объясняет, почему счёт у юристов. */
function Dispatches({ invoice, query }: { invoice: Invoice; query: QueryOf<DunningAction[]> }) {
  const { t, locale } = useApp()
  const steps = [...(query.data ?? [])].sort((a, b) => a.daysOverdue - b.daysOverdue)
  return (
    <>
      <div className="list-item">
        <Icon name="mail" className="muted" />
        <div className="stack" style={{ flex: 1, gap: 0 }}>
          {invoice.sentAt ? (
            <>
              <span>{t('record.inv.sent')}</span>
              <span className="t-xs muted">{formatMoment(invoice.sentAt, locale)}</span>
            </>
          ) : (
            <span className="secondary">{t('record.inv.notSent')}</span>
          )}
        </div>
      </div>
      {query.isLoading && <div className="panel-b muted">{t('app.loading')}</div>}
      {query.isError && <StateView compact kind="error" title={t('record.inv.dunning.error')} />}
      {steps.map((step) => (
        <div key={step.id} className="list-item">
          <Icon name={step.executed ? 'check' : 'clock'} className="muted" />
          <div className="stack" style={{ flex: 1, gap: 0 }}>
            <span className={step.executed ? undefined : 'secondary'}>{t(`dunning.kind.${step.kind}`)}</span>
            <span className="t-xs muted">
              {[
                t('card.inv.dunning.days', { days: step.daysOverdue }),
                // Scheduled and done are different facts: a step written by the sweep
                // but not worked off is to do, not done.
                step.executed && step.executedAt
                  ? t('record.inv.dunning.done', { at: formatMoment(step.executedAt, locale) })
                  : t('record.inv.dunning.planned', { date: formatDate(step.scheduledOn, locale) }),
              ].join(' · ')}
            </span>
          </div>
        </div>
      ))}
      {!query.isLoading && !query.isError && steps.length === 0 && <div className="panel-b secondary t-sm">{t('card.inv.dunning.empty')}</div>}
    </>
  )
}

function DunningTable({ query }: { query: QueryOf<DunningAction[]> }) {
  const { t, locale } = useApp()
  if (query.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (query.isError) return <StateView compact kind="error" title={t('record.inv.dunning.error')} />
  const steps = [...(query.data ?? [])].sort((a, b) => a.daysOverdue - b.daysOverdue)
  if (steps.length === 0) return <div className="panel-b secondary">{t('card.inv.dunning.empty')}</div>
  return (
    <table className="tbl" aria-label={t('card.tab.invoiceDunning')}>
      <thead>
        <tr>
          <th>{t('card.inv.dunning.step')}</th>
          <th>{t('card.inv.dunning.kind')}</th>
          <th>{t('card.inv.dunning.scheduled')}</th>
          <th>{t('card.inv.dunning.executed')}</th>
          <th>{t('card.inv.dunning.result')}</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((step) => (
          <tr key={step.id}>
            <td>{t('card.inv.dunning.days', { days: step.daysOverdue })}</td>
            <td>{t(`dunning.kind.${step.kind}`)}</td>
            <td>{formatDate(step.scheduledOn, locale)}</td>
            <td>{step.executed && step.executedAt ? formatMoment(step.executedAt, locale) : t('card.inv.dunning.pending')}</td>
            <td className="wrap">{step.result ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ------------------------------------------------------------------ документы

/** Первичные документы по счёту — механизмом списков: область видимости и права — его. */
function InvoiceDocuments({ invoiceId }: { invoiceId: string }) {
  const { t, locale } = useApp()
  const query = useQuery({
    queryKey: ['invoice-documents', invoiceId],
    queryFn: ({ signal }) =>
      fetchPage(
        'billing.document',
        {
          columns: ['number', 'document_kind', 'document_date', 'total', 'currency', 'status'],
          filters: [{ column: 'invoice_id', operator: 'eq', value: invoiceId }],
          sort: [{ column: 'document_date', descending: true }],
          pageSize: 20,
        },
        null,
        signal,
      ),
    retry: false,
  })
  if (query.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (query.isError) return <StateView compact kind="error" title={t('record.inv.documents.error')} />
  const rows = query.data?.rows ?? []
  if (rows.length === 0) return <div className="panel-b secondary">{t('record.inv.documents.empty')}</div>
  return (
    <>
      {rows.map((row) => (
        <div key={row.id} className="list-item">
          <div className="stack" style={{ flex: 1, gap: 0 }}>
            <Link to={`${listPath('billing.document', '/documents')}/${row.id}`} className="mono">
              {String(row.number ?? '')}
            </Link>
            <span className="t-xs muted">
              {[
                typeof row.document_date === 'string' ? formatDate(row.document_date, locale) : null,
                typeof row.total === 'number' && typeof row.currency === 'string' ? formatMoney(row.total, row.currency, locale) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          <CellValue
            values={row}
            column="status"
            descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }}
            entity="billing.document"
          />
        </div>
      ))}
    </>
  )
}

